import React, { useEffect, useState } from 'react';

type Weather = { temperature_2m: number; weather_code: number; is_day: number; time: string; sunrise?: string; sunset?: string };
export type WeatherLocation = { latitude: number; longitude: number; label: string };
export const ROME: WeatherLocation = { latitude: 41.9028, longitude: 12.4964, label: 'Roma' };
const TTL = 20 * 60 * 1000;

/** "YYYY-MM-DDTHH:mm" in ora italiana, confrontabile con sunrise/sunset di Open-Meteo (timezone=Europe/Rome). */
export const romeStamp = (d: Date): string => d.toLocaleString('sv-SE', { timeZone: 'Europe/Rome', hour12: false }).slice(0, 16).replace(' ', 'T');

/** Giorno o notte adesso: tra alba e tramonto del giorno (al minuto), altrimenti il flag is_day del meteo. */
export function isDaylight(now: Date, w: Pick<Weather, 'is_day' | 'sunrise' | 'sunset'>): boolean {
  if (w.sunrise && w.sunset) { const t = romeStamp(now); return t >= w.sunrise.slice(0, 16) && t < w.sunset.slice(0, 16); }
  return w.is_day === 1;
}
// cache per coordinate: la dashboard admin (Roma) e l'area associativa (residenza) non si sovrascrivono
const cache = new Map<string, { value: Weather; at: number }>();
const cacheKey = (l: WeatherLocation) => `${l.latitude.toFixed(3)},${l.longitude.toFixed(3)}`;
export const resetWeatherCache = () => cache.clear();
export function weatherAppearance(code: number) {
  if (code >= 95) return { kind: 'storm', label: 'Temporale' };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { kind: 'snow', label: 'Neve' };
  if (code >= 51) return { kind: 'rain', label: 'Pioggia' };
  if ([45, 48].includes(code)) return { kind: 'fog', label: 'Nebbia' };
  if (code === 1 || code === 2) return { kind: 'cloud', label: 'Parzialmente nuvoloso' };
  if (code === 3) return { kind: 'cloud', label: 'Coperto' };
  return { kind: 'clear', label: 'Sereno' };
}
/* Codice WMO di Open-Meteo → scena Meteocons Fill (public/weather-icons).
   Intensità e tipo distinti: "extreme" per pioggia/neve forti e rovesci violenti,
   scena completa "starry-night" per la notte serena (niente icone sovrapposte). */
export function weatherIcon(code: number, isDay: number) {
  const period = isDay === 1 ? 'day' : 'night';
  if (code >= 96) return `extreme-thunderstorms-extreme-${period}-rain`;
  if (code === 95) return `thunderstorms-overcast-${period}-rain`;
  if ([75, 86].includes(code)) return `extreme-${period}-snow`;
  if ([71, 73, 77, 85].includes(code)) return `overcast-${period}-snow`;
  if ([56, 57, 66, 67].includes(code)) return `overcast-${period}-sleet`;
  if ([51, 53, 55].includes(code)) return `overcast-${period}-drizzle`;
  if ([65, 82].includes(code)) return `extreme-${period}-rain`;
  if ([80, 81].includes(code)) return `partly-cloudy-${period}-rain`;
  if ([61, 63].includes(code)) return `overcast-${period}-rain`;
  if ([45, 48].includes(code)) return `fog-${period}`;
  if (code === 3) return `overcast-${period}`;
  if (code === 2) return `partly-cloudy-${period}`;
  if (code === 1) return `mostly-clear-${period}`;
  return isDay === 1 ? 'clear-day' : 'starry-night';
}
/** Saluto per ora italiana: la notte (0-5) resta notte, coerente con la scena del meteo. */
export function greetingFor(hour: number): string {
  if (hour < 6) return 'Buonanotte';
  if (hour < 13) return 'Buongiorno';
  if (hour < 18) return 'Buon pomeriggio';
  return 'Buonasera';
}
export const romeHour = (d: Date) => Number(d.toLocaleTimeString('it-IT', { timeZone: 'Europe/Rome', hour: '2-digit', hourCycle: 'h23' }));

/** Orologio vivo (1 s). */
export function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/** Meteo attuale + alba/tramonto per una posizione, aggiornato ogni 20 minuti (cache condivisa per coordinate). */
export function useWeather(location: WeatherLocation) {
  const key = cacheKey(location);
  const [weather, setWeather] = useState<Weather | null>(() => { const c = cache.get(key); return c && Date.now() - c.at < TTL ? c.value : null; });
  const [unavailable, setUnavailable] = useState(false);
  const { latitude, longitude } = location;
  useEffect(() => {
    let disposed = false;
    let controller: AbortController | undefined;
    const refresh = async () => {
      if (document.visibilityState === 'hidden') return;
      const c = cache.get(key);
      if (c && Date.now() - c.at < TTL) { setWeather(c.value); return; }
      controller?.abort();
      const request = new AbortController();
      controller = request;
      const timeout = setTimeout(() => request.abort(), 10000);
      try {
        const response = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code,is_day&daily=sunrise,sunset&forecast_days=1&timezone=Europe%2FRome`, { signal: request.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Weather unavailable');
        const { current, daily } = await response.json();
        if (!current || !Number.isFinite(current.temperature_2m) || !Number.isFinite(current.weather_code) || ![0, 1].includes(current.is_day) || typeof current.time !== 'string') throw new Error('Invalid weather');
        // alba/tramonto del giorno: la scena cambia al minuto giusto, anche se il meteo è in cache
        const value: Weather = { ...current, sunrise: daily?.sunrise?.[0], sunset: daily?.sunset?.[0] };
        if (!disposed) { cache.set(key, { value, at: Date.now() }); setWeather(value); setUnavailable(false); }
      } catch { if (!disposed) { setWeather(null); setUnavailable(true); } }
      finally { clearTimeout(timeout); }
    };
    refresh();
    const timer = setInterval(refresh, TTL);
    document.addEventListener('visibilitychange', refresh);
    return () => { disposed = true; controller?.abort(); clearInterval(timer); document.removeEventListener('visibilitychange', refresh); };
  }, [key, latitude, longitude]);
  return { weather, unavailable };
}

/** Scena Meteocons animata (statica con prefers-reduced-motion). Stile: .dash-weather-scene o classe passata. */
export const WeatherScene: React.FC<{ weather: Weather; now: Date; className?: string }> = ({ weather, now, className = 'dash-weather-scene' }) => {
  const day = isDaylight(now, weather);
  const icon = weatherIcon(weather.weather_code, day ? 1 : 0);
  return <div className={`${className}${day ? '' : ' weather-night'}`} aria-hidden="true">
    <div className="dash-weather-glow" />
    <picture className="dash-weather-main-icon">
      <source media="(prefers-reduced-motion: reduce)" srcSet={`/weather-icons/static/${icon}.svg`} />
      <img src={`/weather-icons/animated/${icon}.svg`} alt="" width={220} height={220} />
    </picture>
  </div>;
};

/** Riga "Città · 19°C · Sereno" con orario e credito Open-Meteo. */
export const WeatherInfo: React.FC<{ weather: Weather | null; unavailable: boolean; label: string; className?: string }> = ({ weather, unavailable, label, className = 'dash-weather-info' }) => {
  const appearance = weather ? weatherAppearance(weather.weather_code) : null;
  return <div className={className}>{weather && appearance ? <><strong>{label} · {Math.round(weather.temperature_2m)}°C</strong> · {appearance.label}<small>Aggiornato alle {weather.time.slice(11, 16)}</small></> : <span>{label} · {unavailable ? 'Meteo non disponibile' : 'Caricamento meteo…'}</span>}</div>;
};

export const DashboardWelcome: React.FC<{ name: string; location?: WeatherLocation; children: React.ReactNode }> = ({ name, location = ROME, children }) => {
  const now = useClock();
  const { weather, unavailable } = useWeather(location);
  const time = now.toLocaleTimeString('it-IT', { timeZone: 'Europe/Rome', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const greeting = greetingFor(romeHour(now));
  return <div className="dash-welcome-copy">
    {weather && <WeatherScene weather={weather} now={now} />}
    <div className="dash-welcome-foreground">
      <div className="dash-welcome-date">{now.toLocaleDateString('it-IT', { timeZone: 'Europe/Rome', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}<time dateTime={now.toISOString()}>{time}</time></div>
      <h1>{greeting}{name ? `, ${name}` : ''}<span>.</span></h1>
      <p>Una visione chiara. La prossima azione, subito.</p>
      {children}
      <WeatherInfo weather={weather} unavailable={unavailable} label={location.label} />
    </div>
  </div>;
};
